"use client";

import { useState } from "react";
import {
  Check,
  ChevronDown,
  Code2,
  Copy,
  FileText,
  ImageDown,
  Loader2,
  Lock,
  Mail,
  Music,
  Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useSite } from "@/hooks/use-site";
import { blogPostCanonicalUrl } from "@/lib/blog-seo";
import { resolveLicense, type LicenseId } from "@/lib/license";
import { SITE_ORIGIN } from "@/lib/locale-registry";
import type { RepublishPayload } from "@/lib/republish-kit";
import { localizeUrl } from "@/lib/site";

// Payload image URLs are absolute (canonical origin) so they travel inside the
// copyable HTML. For the in-browser zip we fetch our own images via their
// same-origin path — works on whatever host the page is served from (prod or
// preview) and avoids cross-origin failures. External URLs are left as-is.
function sameOriginFetchUrl(src: string): string {
  try {
    const u = new URL(src);
    if (u.origin === SITE_ORIGIN || (typeof location !== "undefined" && u.origin === location.origin)) {
      return u.pathname + u.search;
    }
  } catch {
    /* not a parseable absolute URL — use as-is */
  }
  return src;
}

// Copy to clipboard with a legacy fallback — the async Clipboard API is blocked in
// insecure contexts and some older/embedded browsers, so fall back to a hidden
// textarea + execCommand so the button still works (and gives feedback) everywhere.
async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    /* fall through to legacy path */
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    textarea.remove();
    return ok;
  } catch {
    return false;
  }
}

// One-click "republish kit" at the foot of each post. The link is built client-side;
// the Markdown/HTML/image-URL payload is fetched once when the panel opens (so copy
// clicks stay synchronous). Image zips are built in-browser with a lazily-imported
// JSZip so it never weighs down the main bundle. Hidden on all-rights-reserved posts.
export function RepublishKit({
  slug,
  license,
  hasAudio = false,
}: {
  slug: string;
  license: LicenseId;
  hasAudio?: boolean;
}) {
  const { site, messages, href } = useSite();
  const canonicalUrl = blogPostCanonicalUrl({ locale: site.locale, slug });
  const contactHref = localizeUrl("/contacts", site.locale, href);

  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<RepublishPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [zipBusy, setZipBusy] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);

  if (!resolveLicense(license).republishable) {
    return (
      <div className="mt-6 flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p>
          {messages.republishReservedNotice}{" "}
          <a className="font-medium text-primary hover:underline" href={contactHref}>
            {messages.republishContact}
          </a>
        </p>
      </div>
    );
  }

  const loadPayload = async () => {
    if (payload || loading) return;
    setLoading(true);
    try {
      const res = await fetch(localizeUrl(`/blog/${slug}/republish.json`, site.locale, href));
      if (res.ok) setPayload((await res.json()) as RepublishPayload);
    } finally {
      setLoading(false);
    }
  };

  const flash = (key: string) => {
    setCopied(key);
    setTimeout(() => setCopied((current) => (current === key ? null : current)), 2000);
  };

  const copy = async (key: string, text: string) => {
    if (await writeClipboard(text)) flash(key);
  };

  const download = (filename: string, content: BlobPart, type: string) => {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadImages = async () => {
    if (!payload?.images.length || zipBusy) return;
    setZipBusy(true);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      await Promise.all(
        payload.images.map(async (src) => {
          try {
            const res = await fetch(sameOriginFetchUrl(src));
            if (!res.ok) return;
            const name = src.split("/").pop()?.split(/[?#]/)[0] || "image";
            zip.file(name, await res.blob());
          } catch {
            /* skip images that fail to fetch */
          }
        }),
      );
      const blob = await zip.generateAsync({ type: "blob" });
      download(`${slug}-images.zip`, blob, "application/zip");
    } finally {
      setZipBusy(false);
    }
  };

  const fileName = (src: string) => src.split("/").pop()?.split(/[?#]/)[0] || "file";

  const downloadAudio = async () => {
    const audio = payload?.audio ?? [];
    if (!audio.length || audioBusy) return;
    setAudioBusy(true);
    try {
      if (audio.length === 1) {
        const res = await fetch(sameOriginFetchUrl(audio[0]));
        if (res.ok) {
          download(
            fileName(audio[0]),
            await res.blob(),
            res.headers.get("content-type") || "application/octet-stream",
          );
        }
        return;
      }
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      await Promise.all(
        audio.map(async (src) => {
          try {
            const res = await fetch(sameOriginFetchUrl(src));
            if (res.ok) zip.file(fileName(src), await res.blob());
          } catch {
            /* skip audio that fails to fetch */
          }
        }),
      );
      download(`${slug}-audio.zip`, await zip.generateAsync({ type: "blob" }), "application/zip");
    } finally {
      setAudioBusy(false);
    }
  };

  const pending = !payload;
  const copiedCls = (key: string) =>
    copied === key ? "border-green-600/40 text-green-600 hover:text-green-600" : "";
  const copyIcon = (key: string) =>
    loading ? (
      <Loader2 className="animate-spin" />
    ) : copied === key ? (
      <Check className="animate-in zoom-in-50 duration-200" />
    ) : (
      <Copy />
    );
  const copyLabel = (key: string, label: string) =>
    copied === key ? messages.republishCopied : label;

  return (
    <Collapsible
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) void loadPayload();
      }}
      className="mt-6 overflow-hidden rounded-lg border border-border bg-muted/40"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-foreground/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <span className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-primary" />
            {messages.republishKitToggle}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="collapsible-content">
        <div className="animate-in fade-in slide-in-from-top-1 px-4 pb-4 duration-300">
          <p className="mb-3 text-xs text-muted-foreground">{messages.republishKitIntro}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className={copiedCls("link")}
              onClick={() => void copy("link", canonicalUrl)}
            >
              {copied === "link" ? (
                <Check className="animate-in zoom-in-50 duration-200" />
              ) : (
                <Copy />
              )}
              {copyLabel("link", messages.republishCopyLink)}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={copiedCls("md")}
              disabled={pending}
              onClick={() => payload && void copy("md", payload.markdown)}
            >
              {copyIcon("md")}
              {copyLabel("md", messages.republishCopyMarkdown)}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={copiedCls("html")}
              disabled={pending}
              onClick={() => payload && void copy("html", payload.html)}
            >
              {copyIcon("html")}
              {copyLabel("html", messages.republishCopyHtml)}
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pending}
              onClick={() => payload && download(`${slug}.md`, payload.markdown, "text/markdown")}
            >
              {loading ? <Loader2 className="animate-spin" /> : <FileText />}
              {messages.republishDownloadMarkdown}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pending}
              onClick={() => payload && download(`${slug}.html`, payload.html, "text/html")}
            >
              {loading ? <Loader2 className="animate-spin" /> : <Code2 />}
              {messages.republishDownloadHtml}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pending || zipBusy || !payload?.images.length}
              onClick={() => void downloadImages()}
            >
              {loading || zipBusy ? <Loader2 className="animate-spin" /> : <ImageDown />}
              {zipBusy ? messages.republishDownloadingImages : messages.republishDownloadImages}
            </Button>
            {hasAudio && (
              <Button
                variant="outline"
                size="sm"
                disabled={pending || audioBusy || !payload?.audio.length}
                onClick={() => void downloadAudio()}
              >
                {loading || audioBusy ? <Loader2 className="animate-spin" /> : <Music />}
                {audioBusy ? messages.republishDownloadingAudio : messages.republishDownloadAudio}
              </Button>
            )}
          </div>
          <a
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
            href={contactHref}
          >
            <Mail className="h-3.5 w-3.5" />
            {messages.republishContact}
          </a>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
